
import $ from 'jquery';
import _ from 'lodash';

import * as Verification from './Validation';
import {
	checkLength,
	checkMinLength,
	validName,
	validEmail,
	validNumber,
	validPasswordCharacters,
	valueMatchesWithField,
	validInputCharacters,
	validPluscard
} from '../general/Validation';
import {
	showError,
	hideError
} from '../general/Error';

import { asyncValidateWaterfall } from './CheckoutUtil';

const onlyUnique = ( value, index, array ) => array.indexOf( value ) === index;

class BillingForm {

	constructor( formEl ) {
		this.formEl = formEl;

		// Add Event Listeners.
		this.formEl.addEventListener( 'submit', ( evt ) => this.onSubmit( evt ) );

		const inputEls = Array.from( this.formEl.elements );

		// Disable native Browser validation.
		if ( this.formEl.noValidate ) {
			inputEls.forEach( ( inputEl ) => inputEl.addEventListener( 'invalid', ( evt ) => evt.preventDefault() ) );
		}

		const eventList = ['change', 'input', 'focusout', 'validate_input.acl'];
		eventList.forEach( eventName => {

			this.formEl.addEventListener( eventName, ( evt ) => {

				// Delegate to inputs.
				if ( evt.target.matches( 'input, select, textarea' ) ) {
					this.handleChangeEvent( evt );
				}
			} );
		} );

		this.formEl.addEventListener( 'validated.acl', ( evt ) => {

			// Delegate to inputs.
			if ( evt.target.matches( 'input, select, textarea' ) ) {
				this.showMessages( evt.target );
			}
		} );
	}

	handleChangeEvent( evt ) {
		const targetEl = evt.target;

		// Enable Validation on focusout.
		if ( 'focusout' === evt.type ) {
			targetEl.dataset.displayValidation = true;
		}

		// Get all Inputs inside their respective input_row.
		const inputRowEl = targetEl.closest( '.input_row, .o-field' );
		if ( inputRowEl ) {
			inputRowEl.querySelectorAll( 'input, select, textarea' ).forEach( ( inputEl ) => {

				// Validate Element, if validation is enabled.
				if ( inputEl.matches( ':user-invalid' ) || inputEl.dataset.displayValidation ) {
					this.validateElement( inputEl );
				}
			} );
		}
	}

	isLoading = false;
	setLoading( buttonEl, isLoading = true ) {
		this.isLoading = !!isLoading;

		// Add loading indicator to button.
		const buttonEls = Array.from( this.formEl.querySelectorAll( '.form_button' ) ).concat( buttonEl || [] );
		buttonEls.forEach( buttonEl => buttonEl.classList.toggle( 'en_button--loading', isLoading ) );
	}

	onSubmit = ( evt ) => {

		// Stop here if the submit event has already been prevented.
		if ( evt.defaultPrevented ) {
			return;
		}

		// Prevent Default, so we can validate async.
		evt.preventDefault();

		// Ignore multi-submits, only handle the first one.
		if ( this.isLoading ) {
			return;
		}

		this.setLoading( evt.submitter, true );
		return Promise.resolve( this.validateForm() )
			.catch( err => {
				if ( err ) {
					console.error( err );
				}
				return false;
			} )
			.then( isValid => {

				// Submit Form again, without JS checks.
				if ( isValid ) {
					if ( this.formEl.getAttribute( 'data-action' ) ) {
						this.formEl.setAttribute( 'action', this.formEl.getAttribute( 'data-action' ) );
					}

					// Native submit - send form
					this.formEl.submit();

					// Make resubmit available after 10 seconds - we can surmise something went very wrong.
					setTimeout( () => this.setLoading( evt.submitter,false ), 10000 );
				} else {
					this.setLoading( evt.submitter,false );
					this.scrollToFirstError();
				}

				return isValid;
			} );
	};

	/**
	 * validate the whole form, asynchronously
	 * @return Promise A promise, that resolves to true, if the form is valid.
	 */
	validateForm() {

		const checks = [
			() => this.validateFields(),
			() => Verification.verifyEmail( this.formEl.querySelectorAll( '.verifyemail' ) ),
			() => Verification.verifyPluscard( this.formEl.querySelectorAll( '.verifypluscard_section' ) ),
			() => Verification.verifyPhone( this.formEl.querySelectorAll( '.verifyphone' ), this.formEl.querySelector( '.countryTxt_pp' )?.value ),
			() => Verification.verifyAddress( this.formEl.querySelectorAll( '.verifyaddress' ) )
		];

		// Validate each check in Order and cancel on first error.
		return asyncValidateWaterfall( checks );
	};

	/**
	 * validate all inputs in the current form.
	 */
	validateFields() {

		this.formEl.querySelectorAll( 'input, select, textarea' ).forEach( inputEl => {

			// Enable validation for inputEl.
			inputEl.dataset.displayValidation = true;

			// Validate inputEl
			this.validateElement( inputEl );
		} )

		return this.formEl.reportValidity();
	};

	/**
	 * validate a single HTML Input Element.
	 * these are the synchronous checks.
	 */
	validateElement( inputEl ) {

		const value = inputEl.value;
		const checks = [];
		const validationList = String( inputEl.dataset?.validate ?? '' ).split( '|' );

		// We re-validate the Element here, for that we need to reset its validity.
		inputEl.setCustomValidity( '' );

		// Remove disabled elements from checklist.
		if ( inputEl.disabled ) {
			return true;
		}

		// Custom required check first, so we can control the error message.
		if ( inputEl.required ) {
			checks.push( [ 'required', () => checkLength( value, 0 ) ] );
		}

		// Check native HTML Validation first.
		checks.push( [ 'valid', () => inputEl.checkValidity() ] );

		const canHaveSpecialChars = [ 'validateemail', 'verifyphone', 'validatepwd', 'validatepwdconfirm' ].some( ( className ) => inputEl.classList.contains( className ) );
		const hasSpecialValidation = [ 'email', 'phone', 'password', 'passwordconfirm' ].some( ( checkName ) => validationList.includes( checkName ) );
		if ( inputEl.type === 'hidden' || canHaveSpecialChars || hasSpecialValidation ) {
			// Do nothing.
		} else {
			checks.push( [ 'validinputcharacters', () => validInputCharacters( value ) ] )
		}

		// Check if inputEl has value.
		if ( inputEl.classList.contains( 'required' ) || validationList.includes( 'required' ) ) {
			checks.push( [ 'required', () => checkLength( value, 0 ) ] );
		}

		// Check if inputEl has value.
		if ( inputEl.classList.contains( 'valuecheck' ) || validationList.includes( 'value' ) ) {
			checks.push( [ 'valuecheck', () => checkLength( value, 0 ) ] );
		}

		// Check maxlength.
		if ( inputEl.classList.contains( 'validatelength' ) || validationList.includes( 'length' ) ) {
			checks.push( [ 'validatelength', () => ! checkLength( value, inputEl.maxLength ) ] );
		}

		// Check minlength.
		if ( inputEl.classList.contains( 'validateminlength' ) || validationList.includes( 'minlength' ) ) {
			checks.push( [ 'validatelength', () => ! checkMinLength( value, inputEl.minLength ) ] );
		}

		// Check if all characters are numbers.
		if ( inputEl.classList.contains( 'numberinput' ) || validationList.includes( 'number' ) ) {
			checks.push( [ 'numberinput', () => validNumber( value ) ] );
		}

		// Check Characters in the Name.
		if ( inputEl.classList.contains( 'validatename' ) || validationList.includes( 'name' ) ) {
			checks.push( [ 'validatename', () => validName( value ) ] );
		}

		// Check E-Mail Format.
		if ( inputEl.classList.contains( 'validateemail' ) || validationList.includes( 'email' ) ) {
			checks.push( [ 'validateemail', () => validEmail( value ) ] );
		}

		// Check Pluscardnumber.
		if ( inputEl.classList.contains( 'validateplc' ) || validationList.includes( 'plc' ) ) {
			checks.push( [ 'validateplc', () => validPluscard( value ) ] );
		}

		// Check Password.
		if ( inputEl.classList.contains( 'validatepwd' ) || validationList.includes( 'password' ) ) {
			checks.push( [ 'validatepwd', () => validPasswordCharacters( value, true ) ] );
		}

		// Check Password.
		if ( inputEl.classList.contains( 'validatepwdconfirm' ) || validationList.includes( 'passwordconfirm' ) ) {
			checks.push( [ 'validatepwdconfirm', () => valueMatchesWithField( value, inputEl.dataset.confirmField ) ]  );
		}

		// Go through each check we have added, at the end we should have true or false result.
		const isValid = checks.reduce( ( isValid, [checkName, checkCallback] ) => {

			// When we failed a previous validation, we skip to the end.
			if ( ! isValid ) {
				return false;
			}

			// When the Check fails we add an error.
			if ( ! checkCallback() ) {
				this.addCustomError( inputEl, checkName );
				return false;
			}

			return true;
		}, true );

		// Trigger Validation Message Display.
		if ( isValid ) {
			// Allow other scripts to run validation here.
			$( inputEl ).trigger( 'validatelement.acl' );
			inputEl.dispatchEvent( new CustomEvent( 'valid_element.acl', { bubbles: true } ) );
		}

		// Show Error Messages for this field.
		inputEl.dispatchEvent( new CustomEvent( 'validated.acl', { bubbles: true } ) );

		return isValid && inputEl.checkValidity();
	};

	addCustomError = ( inputEl, failedCheck ) => {
		console.log ( failedCheck )

		// Valid already comes with its own error message.
		if ( 'valid' === failedCheck ) {
			return false;
		}

		// Get global Error Message, if any exist.
		const globalEls = Array.from( document.querySelectorAll( '.acl-global-error-message' ) );
		const globalMessageEl = globalEls.find( messageEl => String( messageEl.dataset.validationMessageFor ?? '' ).split( '|' ).includes( failedCheck ) );
		const defaultMessageEl = globalEls.find( messageEl => String( messageEl.dataset.validationMessageFor ?? '' ).split( '|' ).includes( 'default' ) );

		// Errors are visible on the row.
		const inputRowEl = inputEl.closest( '.input_row, .o-field' );

		// Try to get the error message from among the elements input row messages.
		const customEls = Array.from( inputRowEl?.querySelectorAll( '.input_row_message, .o-field__message' ) ?? [] );
		const customMessageEl = customEls.find( messageEl => String( messageEl.dataset.validationMessageFor ?? '' ).split( '|' ).includes( failedCheck ) );

		// Set Default Error for the Element, so we preserve the message, even if we override the HTML
		if ( inputRowEl && ! inputRowEl.defaultErrorMessage ) {
			const firstMessageEl = customEls[0];
			inputRowEl.defaultErrorMessage ??= firstMessageEl?.innerText
		}

		// First message wins.
		let errorMessage = 'Ein Fehler ist aufgetreten!';
		if ( customMessageEl ) {
			errorMessage = this.getMessageFromEl( customMessageEl );
		} else if ( globalMessageEl ) {
			errorMessage = this.getMessageFromEl( globalMessageEl );
		} else if( inputRowEl && inputRowEl.defaultErrorMessage ) {
			errorMessage = inputRowEl && inputRowEl.defaultErrorMessage;
		} else if ( defaultMessageEl ) {
			errorMessage = this.getMessageFromEl( defaultMessageEl );
		} else {
			errorMessage = 'Eingabe ungültig!';
		}

		// Set Custom Error Message.
		inputEl.setCustomValidity( String( errorMessage || '' ).trim() );
	};

	getMessageFromEl( messageEl ) {
		if ( 'TEMPLATE' === messageEl.nodeName ) {
			return messageEl.content.textContent;
		}
		return messageEl.innerText;
	}


	showMessages = ( inputEl ) => {

		// Get all inputs in the current row.
		const inputRowEl = inputEl.closest( '.input_row' );
		const inputEls = Array.from( inputRowEl?.querySelectorAll( 'input, select, textarea' ) ?? [] );

		// Get all Valid and Invalid input elements.
		const validEls = inputEls.filter( inputEl => ! inputEl.matches( ':invalid' ) );
		const invalidEls = inputEls.filter( inputEl => inputEl.matches( ':invalid' ) );

		// First hide the Error from all Valid Elements in the Row.
		validEls.forEach( inputEl => hideError( inputEl ) );

		// Then show the Error on each invalid element.
		// The message is taken from the first invalid element, if available.
		invalidEls.forEach( inputEl => showError( inputEl, inputEl.validationMessage, inputEl.validationState || 'error' ) );

		return invalidEls.length === 0;
	}

	// Only trigger once every 100ms.
	scrollToFirstError = _.debounce(
		() => {

			const wrapperEls = Array.from( this.formEl.querySelectorAll( '[data-state="error"],[data-state="warning"]' ) );
			const inputEls = wrapperEls.reduce( ( carry, wrapperEl ) => [...carry, ...wrapperEl.querySelectorAll( 'input, select, textarea' ) ], [] ).filter( inputEl => ! inputEl.disabled ).filter( onlyUnique );
			const sectionEls = inputEls.map( inputEl => inputEl.closest( 'section, fieldset, form' ) ).filter( onlyUnique );

			sectionEls.forEach( ( sectionEl, index ) => {
				const legendEl = sectionEl.querySelector( 'legend.trigger' );
				if ( legendEl && ! legendEl.classList.contains( 'active' ) ) {
					legendEl.click();
				}

				// Scroll to the first element.
				if ( 0 === index ) {
					window.scrollTo( {
						top: sectionEl.offsetTop - 150,
						behavior: 'smooth',
					} )
				}
			} )
		},
		100
	);

	static initForms = ( elementList ) => {
		return Array.from( elementList ).forEach( function( formEl ) {
			formEl.ACL_BILLING_FORM ??= new BillingForm( formEl );
		} );
	}
}

$( document ).ready( function() {

	// If one of the birthdate fields is set, we require all of them to be set.
	const birthdayInputEls = document.querySelectorAll( '.birthday-date' );
	birthdayInputEls.forEach( inputEl => {
		inputEl.addEventListener( 'change', () => {
			// Those with required class ( not attribute ) should always be required.
			if ( inputEl.matches( '.required' ) ) {
				return;
			}

			const dateEls = Array.from( document.querySelectorAll( '#birth-day, #birth-month, #birth-year' ) );
			birthdayInputEls.forEach( dateEl => {
				// If one of the values is non-empty, require all inputs.
				dateEl.required = dateEls.some( dateEl => !! dateEl.value );
			} );
		} )
	} );

	// Sync Password values into hidden Confirm Field.
	const passwordEl = document.getElementById( 'register_pass' );
	const confirmEl = document.getElementById( 'register_pass_confirm' );
	if ( passwordEl && confirmEl ) {
		[ 'input', 'change', 'focusout' ].forEach( eventName => {
			passwordEl.addEventListener( eventName, () => {
				confirmEl.value = passwordEl.value;
			} );
		} );
	}

	// Billing Forms.
	const formEls = document.querySelectorAll( '#user_form_1, #register_form, #register_form2, #account_changepw, #user_shipping' );
	BillingForm.initForms( formEls );

	document.dispatchEvent( new CustomEvent( 'init_forms.acl' ) );
} );

// Export Form Class.
export default BillingForm;
